import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { CheckCircle, TrendingUp, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const SummaryStep = ({ formData, calculateAmounts }) => {
  const amounts = calculateAmounts();

  const getValidationErrors = () => {
    const errors = [];
    
    if (!formData.project_name?.trim()) {
      errors.push('กรุณากรอกชื่อโปรเจค');
    }
    
    if (!formData.client_name?.trim()) {
      errors.push('กรุณากรอกชื่อลูกค้า');
    }
    
    if (!formData.project_price || formData.project_price <= 0) {
      errors.push('กรุณากรอกราคาโปรเจค');
    }
    
    if (!formData.payment_schedule || formData.payment_schedule.length === 0) {
      errors.push('กรุณาเพิ่มงวดชำระอย่างน้อย 1 งวด');
    }
    
    return errors;
  };

  const validationErrors = getValidationErrors();
  const isValid = validationErrors.length === 0;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-3">
          <CheckCircle className="h-6 w-6 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">สรุปและบันทึก</h2>
        <p className="text-muted-foreground text-sm">ตรวจสอบข้อมูลและบันทึกโปรเจค</p>
      </div>

      {/* Validation Status */}
      <Card className={`border-2 ${isValid ? 'border-emerald-200' : 'border-red-200'}`}>
        <CardHeader className={`${isValid ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <CardTitle className={`flex items-center gap-2 ${isValid ? 'text-emerald-800' : 'text-red-800'}`}>
            {isValid ? (
              <>
                <CheckCircle className="h-5 w-5" />
                ข้อมูลครบถ้วน พร้อมบันทึก
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5" />
                ข้อมูลไม่ครบถ้วน
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {!isValid && (
            <div className="space-y-2">
              <p className="text-red-600 font-medium">กรุณาแก้ไขข้อมูลต่อไปนี้:</p>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index} className="text-red-600">{error}</li>
                ))}
              </ul>
            </div>
          )}
          
          {isValid && (
            <div className="text-emerald-600">
              <p className="font-medium">ข้อมูลครบถ้วนแล้ว สามารถบันทึกโปรเจคได้</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Summary */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <TrendingUp className="h-5 w-5" />
            สรุปโปรเจค
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">ข้อมูลพื้นฐาน</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ชื่อโปรเจค:</span>
                  <span className="font-medium">{formData.project_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ชื่อลูกค้า:</span>
                  <span className="font-medium">{formData.client_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ชื่อ Sale:</span>
                  <span className="font-medium">{formData.sales_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">จำนวนงวด:</span>
                  <span className="font-medium">{(formData.payment_schedule || []).length} งวด</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">จำนวน Developer:</span>
                  <span className="font-medium">{(formData.developer_payments || []).length} คน</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">สรุปการเงิน</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ราคาโปรเจค:</span>
                  <span className="font-medium">{formatCurrency(amounts.priceBeforeVat)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">เงินเข้าจริง:</span>
                  <span className="font-medium text-green-600">{formatCurrency(amounts.actualReceived)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">รายได้บริษัท:</span>
                  <span className="font-medium text-blue-600">
                    {formatCurrency(amounts.totalCompanyFeeAmount + amounts.totalSalesCommissionAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ค่าใช้จ่ายอื่นๆ:</span>
                  <span className="font-medium text-red-600">{formatCurrency(amounts.totalOtherExpensesAmount)}</span>
                </div>
                <Separator />
                <div className="flex justify-between bg-emerald-50 p-3 rounded-lg">
                  <span className="font-semibold text-emerald-800">คงเหลือสำหรับ Dev:</span>
                  <span className="font-bold text-emerald-800 text-lg">{formatCurrency(amounts.remainingBeforeDev)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Breakdown */}
      <Card className="border-2 border-purple-200">
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <TrendingUp className="h-5 w-5" />
            แบ่งสัดส่วนการเงิน
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>ค่าดำเนินการบริษัท</span>
                <span className="font-medium text-green-600">
                  {formatCurrency(amounts.totalCompanyFeeAmount)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-green-500 h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${amounts.actualReceived > 0 ? (amounts.totalCompanyFeeAmount / amounts.actualReceived * 100) : 0}%` 
                  }}
                ></div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Commission Sale</span>
                <span className="font-medium text-purple-600">
                  {formatCurrency(amounts.totalSalesCommissionAmount)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-purple-500 h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${amounts.actualReceived > 0 ? (amounts.totalSalesCommissionAmount / amounts.actualReceived * 100) : 0}%` 
                  }}
                ></div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>ค่าใช้จ่ายอื่นๆ</span>
                <span className="font-medium text-red-600">
                  {formatCurrency(amounts.totalOtherExpensesAmount)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-red-500 h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${amounts.actualReceived > 0 ? (amounts.totalOtherExpensesAmount / amounts.actualReceived * 100) : 0}%` 
                  }}
                ></div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>สำหรับ Developer</span>
                <span className="font-medium text-blue-600">
                  {formatCurrency(amounts.remainingBeforeDev)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${amounts.actualReceived > 0 ? (amounts.remainingBeforeDev / amounts.actualReceived * 100) : 0}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Schedule Summary */}
      {formData.payment_schedule && formData.payment_schedule.length > 0 && (
        <Card className="border-2 border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <CheckCircle className="h-5 w-5" />
              สรุปงวดชำระ
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{(formData.payment_schedule || []).length}</div>
                <div className="text-sm text-blue-600">งวดทั้งหมด</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {(formData.payment_schedule || []).filter(p => p.paid).length}
                </div>
                <div className="text-sm text-green-600">งวดที่ชำระแล้ว</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {(formData.payment_schedule || []).filter(p => !p.paid).length}
                </div>
                <div className="text-sm text-orange-600">งวดที่รอชำระ</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SummaryStep;
